import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import AuthPageLayout from '../components/ui/AuthPageLayout';
import ValidatedField from '../components/ui/ValidatedField';
import ValidatedSelect from '../components/ui/ValidatedSelect';
import AlertMessage from '../components/ui/AlertMessage';
import { profileService } from '../services/profile';
import {
  ColleagueIcon,
  ConferenceIcon,
  SearchEngineIcon,
  LinkedInIcon,
  TwitterIcon,
  PublicationIcon,
  AdvertisementIcon,
  OtherIcon,
} from '../icons/referral';

// Practice area options from backend
const PRACTICE_AREAS = [
  'Litigation',
  'Corporate',
  'Real Estate',
  'Family',
  'Employment',
  'Regulatory',
  'Intellectual Property (IP)',
  'Court Reporting',
  'Other',
];

// Company size options from backend
const COMPANY_SIZES = [
  '1-10 (Micro)',
  '11-50 (Small)',
  '51-200 (Medium)',
  '201-500 (Mid-size)',
  '501-1,000 (Large)',
  '1,001-5,000 (Enterprise)',
  '5,001+ (Corporate / Global)',
];

// Referral source options from backend
const REFERRAL_SOURCES = [
  'Colleague or peer referral',
  'Legal association or conference',
  'Search engine (Google, etc.)',
  'LinkedIn',
  'X/Twitter',
  'Industry publication or blog',
  'Online advertisement',
  'Other',
];

interface ProfileData {
  companyName: string;
  companyWebsite: string;
  practiceArea: string;
  practiceAreaOther: string;
  companySize: string;
  position: string;
  phone: string;
  tosAccepted: boolean;
  subscribedToUpdates: boolean;
  referralSources: string[];
}

// Helper function to get icon for referral source
const getReferralIcon = (source: string) => {
  const iconProps = { size: 20, className: 'flex-shrink-0' };

  switch (source) {
    case 'Colleague or peer referral':
      return <ColleagueIcon {...iconProps} />;
    case 'Legal association or conference':
      return <ConferenceIcon {...iconProps} />;
    case 'Search engine (Google, etc.)':
      return <SearchEngineIcon {...iconProps} />;
    case 'LinkedIn':
      return <LinkedInIcon {...iconProps} />;
    case 'X/Twitter':
      return <TwitterIcon {...iconProps} />;
    case 'Industry publication or blog':
      return <PublicationIcon {...iconProps} />;
    case 'Online advertisement':
      return <AdvertisementIcon {...iconProps} />;
    case 'Other':
      return <OtherIcon {...iconProps} />;
    default:
      return <OtherIcon {...iconProps} />;
  }
};

const CompleteProfile: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useApp();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const [formData, setFormData] = useState<ProfileData>({
    companyName: '',
    companyWebsite: '',
    practiceArea: '',
    practiceAreaOther: '',
    companySize: '',
    position: '',
    phone: '',
    tosAccepted: false,
    subscribedToUpdates: false,
    referralSources: [],
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleReferralToggle = (source: string) => {
    setFormData((prev) => ({
      ...prev,
      referralSources: prev.referralSources.includes(source)
        ? prev.referralSources.filter((s) => s !== source)
        : [...prev.referralSources, source],
    }));
  };

  const validateStep1 = (): boolean => {
    const errors: Record<string, string[]> = {};

    if (!formData.companyName.trim()) {
      errors.company_name = ['Company name is required'];
    }
    if (!formData.practiceArea) {
      errors.practice_area = ['Practice area is required'];
    }
    if (
      formData.practiceArea === 'Other' &&
      !formData.practiceAreaOther.trim()
    ) {
      errors.practice_area_other = ['Please specify your practice area'];
    }
    if (!formData.companySize) {
      errors.company_size = ['Company size is required'];
    }
    if (!formData.position.trim()) {
      errors.position = ['Position is required'];
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError('Please fill in all required fields');
      return false;
    }

    setFieldErrors({});
    return true;
  };

  const validateStep2 = (): boolean => {
    if (!formData.tosAccepted) {
      setError(
        'You must accept the Terms of Service and Privacy Policy to continue'
      );
      return false;
    }
    return true;
  };

  const handleContinue = () => {
    setError('');

    if (currentStep === 1) {
      if (validateStep1()) {
        setCurrentStep(2);
      }
    } else if (currentStep === 2) {
      if (validateStep2()) {
        setCurrentStep(3);
      }
    }
  };

  const handleBack = () => {
    setError('');
    setFieldErrors({});
    setCurrentStep((prev) => Math.max(1, prev - 1));
  };

  const handleSkipStep3 = () => {
    handleSubmit();
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setError('');

    try {
      await profileService.updateProfile({
        companyName: formData.companyName,
        companyWebsite: formData.companyWebsite || undefined,
        practiceArea: formData.practiceArea,
        practiceAreaOther:
          formData.practiceArea === 'Other'
            ? formData.practiceAreaOther
            : undefined,
        companySize: formData.companySize,
        position: formData.position,
        phone: formData.phone || undefined,
        referralSources: formData.referralSources,
        tosAcceptedAt: new Date().toISOString(),
        subscribedToUpdates: formData.subscribedToUpdates,
      });

      // Update user state to mark profile as completed
      if (state.user) {
        dispatch({
          type: 'SET_USER',
          payload: { ...state.user, profile_completed: true },
        });
      }

      // Redirect based on email confirmation status
      if (state.user?.confirmed_email_at) {
        navigate('/dashboard');
      } else {
        navigate('/check-your-email');
      }
    } catch (err: any) {
      const apiFieldErrors = err.response?.data?.field_errors || {};

      // If API returns field errors, navigate back to step 1 and show them
      if (Object.keys(apiFieldErrors).length > 0) {
        setFieldErrors(apiFieldErrors);
        setCurrentStep(1);
        setError('Please correct the errors in the form');
      } else {
        setError(
          err.response?.data?.errors?.[0] ||
            'Failed to save profile. Please try again.'
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renderProgressBar = () => (
    <div className="flex gap-2 mb-8">
      {[1, 2, 3].map((step) => (
        <div
          key={step}
          className={`h-1 flex-1 rounded-full ${
            step <= currentStep
              ? 'bg-indigo-600 dark:bg-indigo-500'
              : 'bg-gray-300 dark:bg-gray-600'
          }`}
        />
      ))}
    </div>
  );

  const renderStep1 = () => (
    <>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Personal & Company Information
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Please provide your information to complete your account setup.
        </p>
        <div className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          Step 1 of 3
        </div>
      </div>

      {renderProgressBar()}

      <div className="space-y-6">
        <ValidatedField
          id="companyName"
          name="companyName"
          type="text"
          label="Company Name"
          placeholder="Enter your company name"
          value={formData.companyName}
          onChange={handleInputChange}
          required
          serverErrors={fieldErrors.companyName || fieldErrors.company_name}
          hint="Enter the name of your company or organization"
        />

        <ValidatedField
          id="companyWebsite"
          name="companyWebsite"
          type="text"
          label="Company Website"
          placeholder="https://example.com"
          value={formData.companyWebsite}
          onChange={handleInputChange}
          serverErrors={
            fieldErrors.companyWebsite || fieldErrors.company_website
          }
        />

        <ValidatedSelect
          id="practiceArea"
          name="practiceArea"
          label="Practice Area"
          value={formData.practiceArea}
          onChange={handleInputChange}
          required
          serverErrors={fieldErrors.practiceArea || fieldErrors.practice_area}
          hint="Select the primary area of your legal practice"
          options={PRACTICE_AREAS.map((area) => ({
            value: area,
            label: area,
          }))}
          placeholder="Select your practice area"
        />

        {formData.practiceArea === 'Other' && (
          <ValidatedField
            id="practiceAreaOther"
            name="practiceAreaOther"
            type="text"
            label="Please specify your practice area"
            placeholder="Enter your practice area"
            value={formData.practiceAreaOther}
            onChange={handleInputChange}
            required
            serverErrors={
              fieldErrors.practiceAreaOther || fieldErrors.practice_area_other
            }
            hint="Specify your practice area"
          />
        )}

        <ValidatedSelect
          id="companySize"
          name="companySize"
          label="Company Size"
          value={formData.companySize}
          onChange={handleInputChange}
          required
          serverErrors={fieldErrors.companySize || fieldErrors.company_size}
          hint="Select the size of your company or organization"
          options={COMPANY_SIZES.map((size) => ({
            value: size,
            label: size,
          }))}
          placeholder="Select company size"
        />

        <ValidatedField
          id="position"
          name="position"
          type="text"
          label="Position"
          placeholder="e.g., Product Manager, CEO, Lawyer"
          value={formData.position}
          onChange={handleInputChange}
          required
          serverErrors={fieldErrors.position}
          hint="Enter your job title or role"
        />

        <ValidatedField
          id="phone"
          name="phone"
          type="tel"
          label="Phone Number"
          placeholder="+1 (555) 123-4567"
          value={formData.phone}
          onChange={handleInputChange}
          serverErrors={fieldErrors.phone}
        />

        <button
          type="button"
          onClick={handleContinue}
          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Continue
        </button>
      </div>
    </>
  );

  const renderStep2 = () => (
    <>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Terms of Service
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Please review and accept our terms of service to continue.
        </p>
        <div className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          Step 2 of 3
        </div>
      </div>

      {renderProgressBar()}

      <div className="space-y-6">
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-600">
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-6">
            At VeriVox AI, we understand how important your data is. Our
            Toronto-based information security and compliance team uses expert
            oversight, advanced AI technology, and global best practices to
            ensure that your data remains protected at all times.
          </p>

          <div className="space-y-6">
            <section>
              <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
                Infrastructure
              </h4>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                VeriVox AI leverages secure Canadian and global cloud
                infrastructure to ensure data safety and uptime.
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-300 ml-4">
                <li>
                  We use AWS and other compliant providers for hosting with
                  multi-region redundancy and strict access control.
                </li>
                <li>
                  All cloud operations are secured with SSO, MFA, and logged
                  activity monitoring.
                </li>
                <li>
                  Continuous vulnerability scanning and cloud security posture
                  management ensure compliance and fast remediation of issues.
                </li>
              </ul>
            </section>

            <section>
              <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
                Application Security
              </h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-300 ml-4">
                <li>
                  All data transmission is encrypted using HTTPS and TLS
                  protocols; stored data uses AES-256 encryption.
                </li>
                <li>
                  Access control includes two-factor authentication, role-based
                  permissions, and strict account management.
                </li>
                <li>
                  Regular independent penetration tests are conducted annually
                  by certified cybersecurity experts.
                </li>
                <li>
                  All code is reviewed under a Secure Software Development
                  Lifecycle (SSDLC) following OWASP standards.
                </li>
              </ul>
            </section>

            <section>
              <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
                Corporate Security
              </h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-300 ml-4">
                <li>
                  We apply single sign-on (SSO) across corporate systems with
                  enforced two-factor authentication.
                </li>
                <li>
                  All endpoints are encrypted and centrally managed, with the
                  ability to remotely lock or wipe devices.
                </li>
                <li>
                  Our systems include next-generation antivirus (NGAV) and EDR
                  with 24/7 managed threat detection.
                </li>
                <li>
                  Vendor risk management and security reviews ensure that all
                  third-party partners maintain compliance.
                </li>
              </ul>
            </section>

            <section>
              <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
                Human Resources
              </h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-300 ml-4">
                <li>
                  All VeriVox AI employees complete annual information security
                  and privacy training.
                </li>
                <li>
                  We maintain a Security Champions program to reinforce
                  proactive cybersecurity awareness.
                </li>
                <li>
                  Background and identity checks are conducted as allowed by law
                  for new hires handling sensitive data.
                </li>
                <li>
                  Access rights follow the principle of least privilege and are
                  reviewed regularly.
                </li>
              </ul>
            </section>

            <section>
              <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
                Privacy & Data Protection
              </h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-300 ml-4">
                <li>
                  We integrate privacy by design and default in all our products
                  and processes.
                </li>
                <li>
                  Our Privacy Policy clearly explains how your data is
                  collected, used, and stored.
                </li>
                <li>
                  We sign Data Processing Agreements (DPAs) with customers and
                  third parties to ensure lawful data processing.
                </li>
                <li>
                  Data transfers comply with Canadian PIPEDA and international
                  standards including Standard Contractual Clauses (SCCs).
                </li>
                <li>
                  VeriVox AI does not allow government access to personal data
                  unless legally compelled and reviewed by our legal team.
                </li>
              </ul>
            </section>

            <section>
              <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
                Governance & Compliance
              </h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-300 ml-4">
                <li>
                  Our Information Security Management System (ISMS) follows ISO
                  27001:2022 and SOC 2 frameworks.
                </li>
                <li>
                  Risk assessments are performed regularly and reviewed by
                  executive leadership.
                </li>
                <li>
                  Information security policies are reviewed annually and
                  communicated company-wide.
                </li>
                <li>
                  Internal audits and continuous improvement programs ensure
                  accountability and compliance.
                </li>
              </ul>
            </section>

            <section>
              <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
                Contact
              </h4>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                For any security or privacy-related inquiries, contact our team:
              </p>
              <ul className="list-none space-y-1 text-sm text-gray-700 dark:text-gray-300 ml-4">
                <li>Email: security@verivox.ai</li>
                <li>Phone: +1 (416) XXX-XXXX</li>
              </ul>
            </section>

            <div className="mt-6 pt-4 border-t border-gray-300 dark:border-gray-600">
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                Date of last revision: October 2025
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-start space-x-3 p-4 border border-gray-300 dark:border-gray-600 rounded-lg">
            <input
              type="checkbox"
              id="tosAccepted"
              name="tosAccepted"
              checked={formData.tosAccepted}
              onChange={handleInputChange}
              className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label
              htmlFor="tosAccepted"
              className="text-sm text-gray-700 dark:text-gray-300"
            >
              I agree to VeriVox AI's{' '}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 dark:text-indigo-400 hover:underline"
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 dark:text-indigo-400 hover:underline"
              >
                Privacy Policy
              </a>
              .
            </label>
          </div>

          <div className="flex items-start space-x-3 p-4 border border-gray-300 dark:border-gray-600 rounded-lg">
            <input
              type="checkbox"
              id="subscribedToUpdates"
              name="subscribedToUpdates"
              checked={formData.subscribedToUpdates}
              onChange={handleInputChange}
              className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label
              htmlFor="subscribedToUpdates"
              className="text-sm text-gray-700 dark:text-gray-300"
            >
              I want to receive product updates and feature announcements. You
              can unsubscribe at any time.
            </label>
          </div>
        </div>

        <div className="flex gap-4">
          <button
            type="button"
            onClick={handleBack}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleContinue}
            className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Continue
          </button>
        </div>
      </div>
    </>
  );

  const renderStep3 = () => (
    <>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          How did you hear about us?
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Help us understand how you discovered VeriVox AI.
        </p>
        <div className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          Step 3 of 3 (Optional)
        </div>
      </div>

      {renderProgressBar()}

      <div className="space-y-3 mb-6">
        {REFERRAL_SOURCES.map((source) => (
          <button
            key={source}
            onClick={() => handleReferralToggle(source)}
            className={`w-full p-4 text-left border rounded-lg transition-colors flex items-center gap-3 ${
              formData.referralSources.includes(source)
                ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20 dark:border-indigo-500'
                : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <div className="text-gray-600 dark:text-gray-400">
              {getReferralIcon(source)}
            </div>
            <span className="text-gray-900 dark:text-white">{source}</span>
          </button>
        ))}
      </div>

      <div className="flex gap-4">
        <button
          type="button"
          onClick={handleBack}
          className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleSkipStep3}
          className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          Skip
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isLoading}
          className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Submitting...' : 'Finish'}
        </button>
      </div>
    </>
  );

  return (
    <AuthPageLayout
      title="Complete Your Profile"
      description="Just a few more details to get started"
    >
      <div className="space-y-6">
        <AlertMessage type="error" message={error} show={!!error} />

        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
      </div>
    </AuthPageLayout>
  );
};

export default CompleteProfile;
